import React from 'react';
import {
  CheckCircle,
  CircleNotch,
  MinusCircle,
  Pause,
  PencilSimple,
  WarningCircle,
} from '@phosphor-icons/react';

const STATUS_CONFIG = {
  active: {
    icon: CheckCircle,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    dot: 'bg-emerald-400',
    label: 'Active',
    pulse: true,
  },
  running: {
    icon: CircleNotch,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    dot: 'bg-blue-400',
    label: 'Running',
    spin: true,
    pulse: true,
  },
  idle: {
    icon: MinusCircle,
    color: 'text-[var(--text-muted)]',
    bg: 'bg-[var(--surface-3)]',
    border: 'border-[var(--border-default)]',
    dot: 'bg-[var(--text-muted)]',
    label: 'Idle',
  },
  draft: {
    icon: PencilSimple,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    dot: 'bg-amber-400',
    label: 'Draft',
  },
  paused: {
    icon: Pause,
    color: 'text-gray-400',
    bg: 'bg-gray-500/10',
    border: 'border-gray-500/20',
    dot: 'bg-gray-400',
    label: 'Paused',
  },
  error: {
    icon: WarningCircle,
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    border: 'border-red-500/20',
    dot: 'bg-red-400',
    label: 'Error',
  },
  processing: {
    icon: CircleNotch,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    dot: 'bg-blue-400',
    label: 'Processing',
    spin: true,
  },
};

const DEFAULT_CONFIG = STATUS_CONFIG.draft;

/**
 * Renders a status badge with a Phosphor icon.
 * 
 * Variants:
 *  - "badge"   → pill-shaped badge with icon + label (default)
 *  - "dot"     → tiny pulsing dot only (for compact layouts)
 *  - "icon"    → icon-only, no label
 *  - "minimal" → small icon + label, no background
 */
export const AgentStatusIndicator = ({
  status = 'draft',
  variant = 'badge',
  size = 'sm',
  className = '',
}) => {
  const config = STATUS_CONFIG[status] || DEFAULT_CONFIG;
  const StatusIcon = config.icon;

  const sizeMap = {
    xs: { icon: 10, text: 'text-[10px]', dot: 'w-1.5 h-1.5', px: 'px-1.5 py-0.5', gap: 'gap-1' },
    sm: { icon: 13, text: 'text-xs', dot: 'w-2 h-2', px: 'px-2 py-1', gap: 'gap-1.5' },
    md: { icon: 16, text: 'text-sm', dot: 'w-2.5 h-2.5', px: 'px-3 py-1.5', gap: 'gap-2' },
  };
  const s = sizeMap[size] || sizeMap.sm;

  if (variant === 'dot') {
    return (
      <span className={`relative inline-flex ${className}`} data-testid={`status-dot-${status}`}>
        <span className={`${s.dot} rounded-full ${config.dot}`} />
        {config.pulse && (
          <span className={`absolute inset-0 ${s.dot} rounded-full ${config.dot} animate-ping opacity-40`} />
        )}
      </span>
    );
  }

  if (variant === 'icon') {
    return (
      <span className={`inline-flex items-center ${config.color} ${className}`} data-testid={`status-icon-${status}`}>
        <StatusIcon
          size={s.icon}
          weight="duotone"
          className={config.spin ? 'animate-spin' : config.pulse ? 'animate-pulse' : ''}
        />
      </span>
    );
  }

  if (variant === 'minimal') {
    return (
      <span
        className={`inline-flex items-center ${s.gap} ${config.color} ${s.text} font-medium ${className}`}
        data-testid={`status-minimal-${status}`}
      >
        <StatusIcon
          size={s.icon}
          weight="duotone"
          className={config.spin ? 'animate-spin' : ''}
        />
        {config.label}
      </span>
    );
  }

  // Default: badge variant
  return (
    <span
      className={`inline-flex items-center ${s.gap} ${s.px} ${s.text} font-medium rounded-full border ${config.bg} ${config.color} ${config.border} ${className}`}
      data-testid={`status-badge-${status}`}
    >
      <StatusIcon
        size={s.icon}
        weight="duotone"
        className={config.spin ? 'animate-spin' : ''}
      />
      {config.label}
    </span>
  );
};

/**
 * Stat card for the dashboard that displays a status count with its icon.
 */
export const AgentStatusStat = ({ status = 'active', count = 0, loading = false }) => {
  const config = STATUS_CONFIG[status] || DEFAULT_CONFIG;
  const StatusIcon = config.icon;

  return (
    <div
      className={`flex items-center gap-3 rounded-[10px] border ${config.border} bg-[var(--surface-2)] px-4 py-3 transition-all hover:shadow-[var(--shadow-sm)]`}
      data-testid={`status-stat-${status}`}
    >
      <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${config.bg}`}>
        <StatusIcon size={16} weight="duotone" className={config.color} />
      </div>
      <div>
        <span className={`text-xl font-bold ${config.color}`}>
          {loading ? '...' : count}
        </span>
        <p className="text-[10px] text-[var(--text-muted)] font-medium leading-tight">{config.label}</p>
      </div>
    </div>
  );
};

export default AgentStatusIndicator;

/**
 * Derives operational status from agent data and run history.
 * This avoids WebSocket infrastructure — status is inferred from timestamps.
 *
 * Logic:
 *  - If agent.status === 'paused' → 'paused'
 *  - If last run started within 60s and no completion → 'running'
 *  - If last run failed within 10 minutes → 'error'
 *  - If agent.status === 'active' → 'active'
 *  - If agent.status === 'draft' → 'draft'
 *  - Otherwise → 'idle'
 *
 * Future: replace with WebSocket push for real-time status.
 */
export function deriveOperationalStatus(agent) {
  if (!agent) return 'idle';

  const agentStatus = (agent.status || '').toLowerCase();

  if (agentStatus === 'paused') return 'paused';

  // Check for recent run activity
  const lastRunAt = agent.last_run_at || agent.lastRunAt || agent.updated_at;
  if (lastRunAt) {
    const elapsed = Date.now() - new Date(lastRunAt).getTime();
    const elapsedSec = elapsed / 1000;

    // If a run started very recently, consider it running
    if (elapsedSec < 60 && (agent.last_run_status === 'running' || agent.last_run_status === 'processing')) {
      return 'running';
    }

    // If last run failed recently, show error
    if (elapsedSec < 600 && (agent.last_run_status === 'failed' || agent.last_run_status === 'error')) {
      return 'error';
    }
  }

  if (agentStatus === 'active') return 'active';
  if (agentStatus === 'draft') return 'draft';

  return 'idle';
}
